// src/stage2/characters/NPCActor.jsx
import React, { useEffect, useMemo, useRef } from "react";
import * as THREE from "three";
import { useGLTF, useAnimations } from "@react-three/drei";
import { SkeletonUtils } from "three-stdlib";
import ShadowOrbRig from "./ShadowOrbRig";

function filterClipForRig(clip, root) {
  const validNames = new Set();
  root.traverse((o) => validNames.add(o.name));

  const filteredTracks = clip.tracks.filter((t) => {
    const nodeName = t.name.split(".")[0];
    return validNames.has(nodeName);
  });

  return new THREE.AnimationClip(clip.name, clip.duration, filteredTracks);
}

function pickClip(animations = [], preferredName) {
  if (!animations.length) return null;
  if (preferredName) {
    const found = animations.find((a) => a.name === preferredName);
    if (found) return found;
  }
  return animations[0];
}

export default function NPCActor({
  modelUrl,
  forcedIndex = null,          // ✅ keep this
  actionUrls = [],
  preferredClipNames = [],

  cycle = true,
  cycleSeconds = 6.0,
  startIndex = 0,
  cycleDesyncSeconds = 0,
  paused = false,

  shadowOnly = true,
  baseScale = 1,
  shadowScaleXZ = [1, 1],

  orb = true,
  orbColor = "#7df4ff",
  orbSize = 0.45,
  orbPulse = true,
  orbOffset = [0, 0.06, 0],
  orbBoneNames = ["mixamorigHead", "Head", "mixamorigSpine2", "Hips"],
}) {
  const base = useGLTF(modelUrl);
  const rig = useMemo(() => SkeletonUtils.clone(base.scene), [base.scene]);

  const actionGLTFs = actionUrls.map((u) => useGLTF(u));

  const orderedClips = useMemo(() => {
    const out = [];

    for (let i = 0; i < actionGLTFs.length; i++) {
      const g = actionGLTFs[i];
      const preferred = preferredClipNames?.[i];
      const rawClip = pickClip(g?.animations || [], preferred);
      if (!rawClip) continue;

      const filtered = filterClipForRig(rawClip, rig);

      if (!filtered.tracks?.length) {
        console.warn(
          "[NPCActor] Skipping clip with 0 tracks after filtering:",
          rawClip.name,
          "from",
          actionUrls[i]
        );
        continue;
      }

      out.push(filtered);
    }

    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rig, ...actionGLTFs.map((g) => g.animations), ...preferredClipNames]);

  const { actions, mixer } = useAnimations(orderedClips, rig);

  useEffect(() => {
    if (!mixer) return;
    mixer.timeScale = paused ? 0 : 1;
  }, [mixer, paused]);

  const currentActionRef = useRef(null);

  useEffect(() => {
    if (!actions) return;
    if (!orderedClips.length) return;

    let intervalId = null;
    let timeoutId = null;

    const playIndex = (i) => {
      const idx = Math.max(0, Math.min(i, orderedClips.length - 1));
      const clip = orderedClips[idx];
      const next = actions[clip.name];
      if (!next) return;

      const cur = currentActionRef.current;
      if (cur && cur !== next) cur.fadeOut(0.15);

      next.reset();

      // ✅ ensure looping
      next.setLoop(THREE.LoopRepeat, Infinity);
      next.clampWhenFinished = false;

      next.fadeIn(0.15).play();
      currentActionRef.current = next;
    };

    const start = () => {
      // ✅ if forcedIndex is set, play it and DO NOT auto-switch to others
      if (forcedIndex != null) {
        playIndex(forcedIndex);
        return;
      }

      // normal behavior
      let idx = Math.max(0, Math.min(startIndex, orderedClips.length - 1));
      playIndex(idx);

      if (cycle && orderedClips.length > 1) {
        intervalId = window.setInterval(() => {
          idx = (idx + 1) % orderedClips.length;
          playIndex(idx);
        }, Math.max(0.5, cycleSeconds) * 1000);
      }
    };

    if (cycleDesyncSeconds > 0) {
      timeoutId = window.setTimeout(
        start,
        Math.random() * cycleDesyncSeconds * 1000
      );
    } else {
      start();
    }

    return () => {
      if (timeoutId) window.clearTimeout(timeoutId);
      if (intervalId) window.clearInterval(intervalId);
      if (currentActionRef.current) currentActionRef.current.stop();
      currentActionRef.current = null;
    };
  }, [actions, orderedClips, cycle, cycleSeconds, startIndex, cycleDesyncSeconds, forcedIndex]);

  return (
    <ShadowOrbRig
      rig={rig}
      baseScale={baseScale}
      shadowScaleXZ={shadowScaleXZ}
      shadowOnly={shadowOnly}
      orb={orb}
      orbColor={orbColor}
      orbSize={orbSize}
      orbPulse={orbPulse}
      orbOffset={orbOffset}
      orbBoneNames={orbBoneNames}
    >
      <primitive object={rig} />
    </ShadowOrbRig>
  );
}
